import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { getModels } from '@/utils/api'
import { Model } from '@/types/api'
import ModelCard from './ModelCard'
import Spinner from '@/components/common/Spinner'

export interface ModelSelectorProps {
  selectedModel: string | null
  onModelSelect: (modelName: string) => void
}

const ModelSelector = ({ selectedModel, onModelSelect }: ModelSelectorProps) => {
  const [models, setModels] = useState<Model[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadModels()
  }, [])

  const loadModels = async () => {
    try {
      setLoading(true)
      const data = await getModels()
      const filteredModels = data.models.filter(
        (model) => model.name !== 'dwsep_se_a050'
      )
      const modelOrder = [
        'dwsep_se_a075',
        'cascade_tiny',
        'nanoconv_d02',
        'multitask_dw_a075',
      ]
      const sortedModels = [...filteredModels].sort((a, b) => {
        const aIndex = modelOrder.indexOf(a.name)
        const bIndex = modelOrder.indexOf(b.name)
        const safeA = aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex
        const safeB = bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex
        return safeA - safeB
      })
      setModels(sortedModels)

      // Auto-select default model if none selected or filtered out
      const defaultModel =
        sortedModels.find((model) => model.name === data.default)?.name ||
        sortedModels[0]?.name
      if (
        defaultModel &&
        (!selectedModel || !sortedModels.some((model) => model.name === selectedModel))
      ) {
        onModelSelect(defaultModel)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load models')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Spinner size="lg" />
        <p className="mt-4 text-gray-600 dark:text-dark-400">Loading models...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6 bg-red-500/10 border border-red-500/20 rounded-xl text-center">
        <svg
          className="w-12 h-12 text-red-400 mx-auto mb-3"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <p className="text-red-600 dark:text-red-400">{error}</p>
      </div>
    )
  }

  if (models.length === 0) {
    return (
      <div className="p-6 bg-gray-100 dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-xl text-center">
        <p className="text-gray-600 dark:text-dark-400">No models available</p>
      </div>
    )
  }

  return (
    <div>
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-50 mb-1">
          Select Model
        </h3>
        <p className="text-sm text-gray-600 dark:text-dark-400">
          Choose a model based on your accuracy and speed requirements
        </p>
      </div>

      <motion.div
        className="flex flex-nowrap gap-4 overflow-x-auto xl:overflow-visible pb-2 xl:pb-0 -mx-2 xl:mx-0 px-2 xl:px-0 justify-start xl:justify-center"
        initial="hidden"
        animate="visible"
        variants={{
          visible: {
            transition: {
              staggerChildren: 0.05,
            },
          },
        }}
      >
        {models.map((model) => (
          <motion.div
            key={model.name}
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: { opacity: 1, y: 0 },
            }}
            className="flex-shrink-0 min-w-[220px] w-64"
          >
            <ModelCard
              model={model}
              selected={selectedModel === model.name}
              onSelect={onModelSelect}
            />
          </motion.div>
        ))}
      </motion.div>
    </div>
  )
}

export default ModelSelector
